import {
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  Time,
  TimeSlider,
  useMediaState,
  type MediaPlayerProps,
} from '@vidstack/react';
import { useEffect, useRef } from 'react';
import { cn } from '~/lib/utils';
import DefaultPlayButton from './play-button';
import { usePlayerContext } from './player-context';
import SpeedButtons from './speed-buttons';

const Player = ({ src, className, ...props }: Omit<MediaPlayerProps, 'children'>) => {
  // context
  const { media, setMedia } = usePlayerContext();
  // ref
  const ref = useRef<MediaPlayerInstance>(null);
  // state
  const playing = useMediaState('playing', ref);
  // effects
  useEffect(() => {
    if (!media) return;
    if (media === src) return;
    if (playing) ref.current?.pause();
  }, [media, playing, src]);
  // UI
  if (!src) return null;
  return (
    <MediaPlayer
      ref={ref}
      key={src.toString()}
      className={cn('w-full border-none', className)}
      load="visible"
      streamType="on-demand"
      storage="musea"
      playsInline
      src={src}
      {...props}
    >
      <div className="w-full">
        <div className="flex justify-between">
          <DefaultPlayButton />
          <SpeedButtons />
        </div>
        <TimeSlider.Root className="group relative inline-flex h-10 mt-4 mb-1 w-full cursor-pointer touch-none select-none items-center outline-none aria-hidden:hidden">
          <TimeSlider.Track className="relative h-[3px] w-full rounded-sm bg-primary">
            <TimeSlider.TrackFill className="absolute h-full w-[var(--slider-fill)] rounded-sm will-change-[width] bg-secondary" />
            {/* <TimeSlider.Progress className="absolute z-10 h-full w-[var(--slider-progress)] rounded-sm bg-primary will-change-[width]" /> */}
          </TimeSlider.Track>
          <TimeSlider.Thumb className="absolute left-[var(--slider-fill)] top-1/2 z-20 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-secondary ring-white/40 transition-opacity group-data-[active]:opacity-100 group-data-[dragging]:ring-4 will-change-[left]" />
        </TimeSlider.Root>
        <div className="flex w-full justify-between vds-time-group">
          <Time type="current" className="text-xs text-primary" />
          <Time type="duration" className="text-xs text-primary" />
        </div>
        <MediaProvider
          className="mt-6 w-full aspect-video"
          onPlay={() => setMedia(src.toString())}
        />
      </div>
    </MediaPlayer>
  );
};

Player.displayName = 'Player';

export default Player;
